<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccountFeatures } from 'dashboard/composables/useAccountFeatures';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TaskScenarioCard from './TaskScenarioCard.vue';
import TaskScenarioEditorDrawer from './TaskScenarioEditorDrawer.vue';
import PilotBuiltinToolsAPI from 'dashboard/api/pilot/builtinTools';
import UpdateLeadsToolAPI from 'dashboard/api/integrations/mip/update_leads_tool';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { hasMarketingCloudTenantId } = useAccountFeatures();

const injectedAssistantId = inject('assistantId');
const assistantId = computed(
  () => injectedAssistantId?.value || props.assistant?.id
);

const storeScenarios = useMapGetter('pilotScenarios/getRecords');
const scenarios = computed(() => storeScenarios.value || []);

const scenariosCollapsed = ref(
  localStorage.getItem('pilot_tasks_collapsed') === 'true'
);
const fieldOptions = ref([]);
const editingScenario = ref(null);
const editorOpen = ref(false);

const TASK_TEMPLATES = [
  {
    templateKey: 'trial_signup',
    taskType: 'pre_sales',
    title: '线索收集',
    description: '用于处理试用、演示、价格、版本、私有化等售前咨询。',
    icon: 'i-lucide-badge-plus',
    usesFields: true,
    hasHandoff: false,
    triggerConditions: [
      '想试用或演示产品（"能试用么"、"演示一下"）',
      '表达购买意向（"想买"、"多少钱"、"怎么收费"）',
    ],
    taskConfig: {
      opening_message: '您好，很高兴为您介绍产品。我先了解下您的试用需求。',
      completion_message: '信息已记录，我们会尽快安排后续跟进。',
    },
    defaultFieldKeys: ['name', 'phone_number'],
    defaultRequiredFieldKeys: ['name', 'phone_number'],
  },
  {
    templateKey: 'channel_partnership',
    taskType: 'partnership',
    title: '渠道合作',
    description: '用于处理代理、分销、加盟、企业采购等合作咨询。',
    icon: 'i-lucide-handshake',
    usesFields: true,
    hasHandoff: true,
    triggerConditions: [
      '想做代理商、渠道分销、加盟合作、企业战略合作、大批量采购',
    ],
    taskConfig: {
      opening_message: '欢迎咨询合作事宜，我先了解下您的合作需求和基本信息。',
      handoff_message: '合作信息已记录，我们会安排专人尽快与您联系。',
    },
    defaultFieldKeys: ['name', 'phone_number'],
    defaultRequiredFieldKeys: ['name', 'phone_number'],
  },
  {
    templateKey: 'complaint_soothing',
    taskType: 'complaint',
    title: '投诉处理和情绪安抚',
    description: '用于处理用户投诉、激烈不满、退款或赔偿诉求。',
    icon: 'i-lucide-shield-alert',
    usesFields: false,
    hasHandoff: true,
    triggerConditions: [
      '表达愤怒或不满（"太差了"、"垃圾"、"什么破产品"、"什么烂服务"）',
      '投诉或威胁（"我要投诉"、"举报你们"、"起诉"、"曝光你们"）',
      '强烈要求退款或赔偿（"退钱"、"赔偿"）',
      '情绪激动说脏话（用户使用激烈言辞、辱骂）',
      '表达失望或后悔（"早知道不选你们"）',
    ],
    taskConfig: {
      opening_message: '非常抱歉给您带来不好的体验，我们会认真对待您的问题。',
      ask_reason: true,
      handoff_message: '我已经记录了您的反馈，马上为您转人工跟进。',
    },
  },
];

const templateMap = computed(
  () =>
    TASK_TEMPLATES.reduce((acc, template) => {
      acc[template.templateKey] = template;
      return acc;
    }, {})
);

const structuredScenarios = computed(() =>
  scenarios.value.filter(
    scenario =>
      scenario.template_key && scenario.template_key !== 'campaign_benefit_claim'
  )
);
const existingTemplateKeys = computed(
  () => new Set(structuredScenarios.value.map(scenario => scenario.template_key))
);
const enabledCount = computed(
  () => structuredScenarios.value.filter(scenario => scenario.enabled).length
);
const fieldCollectionCount = computed(
  () => structuredScenarios.value.filter(
    scenario => templateMap.value[scenario.template_key]?.usesFields
  ).length
);

const isMipEnabled = computed(() => hasMarketingCloudTenantId.value);
const manageFieldsRoute = computed(() =>
  isMipEnabled.value
    ? { name: 'mip_leads_update_leads_tool', params: { accountId: route.params.accountId } }
    : { name: 'pilot_builtin_tools_update_contact_config', params: { accountId: route.params.accountId } }
);

const loadFieldOptions = async () => {
  if (isMipEnabled.value) {
    const response = await UpdateLeadsToolAPI.getConfig();
    fieldOptions.value = (response.data.fields || []).map(field => ({
      key: field.field_key,
      label: field.display_name || field.field_key,
      description: field.ai_description || '',
    }));
    return;
  }

  const response = await PilotBuiltinToolsAPI.getContactFields();
  const allFields = response.data.contact_fields || [];
  const selectedKeys = response.data.selected_fields || [];
  fieldOptions.value = selectedKeys.map(key => {
    const field = allFields.find(item => item.attribute_key === key);
    return {
      key,
      label: field?.attribute_display_name || key,
      description: field?.attribute_description || '',
    };
  });
};

const fetchData = async () => {
  if (!assistantId.value) return;
  await store.dispatch('pilotScenarios/get', {
    assistantId: assistantId.value,
  });
  await loadFieldOptions();
};

const defaultSelectedFieldKeys = template => {
  if (isMipEnabled.value) return [];

  const availableKeys = new Set(fieldOptions.value.map(field => field.key));
  return (template.defaultFieldKeys || []).filter(key => availableKeys.has(key));
};

const defaultRequiredFieldKeys = template => {
  const selectedKeys = defaultSelectedFieldKeys(template);
  return (template.defaultRequiredFieldKeys || []).filter(key =>
    selectedKeys.includes(key)
  );
};

const closeSuggestedRules = () => {
  scenariosCollapsed.value = true;
  localStorage.setItem('pilot_tasks_collapsed', 'true');
};

const expandSuggestedRules = () => {
  scenariosCollapsed.value = false;
  localStorage.setItem('pilot_tasks_collapsed', 'false');
};

const createTask = async template => {
  try {
    await store.dispatch('pilotScenarios/create', {
      assistantId: assistantId.value,
      title: template.title,
      enabled: true,
      template_key: template.templateKey,
      task_type: template.taskType,
      trigger_conditions: template.triggerConditions,
      selected_field_keys: defaultSelectedFieldKeys(template),
      required_field_keys: defaultRequiredFieldKeys(template),
      task_config: template.taskConfig,
    });
    useAlert('任务已创建');
  } catch (error) {
    useAlert(error?.message || '创建任务失败');
  }
};

const updateStructuredScenario = async payload => {
  try {
    const scenario = structuredScenarios.value.find(item => item.id === payload.id);
    await store.dispatch('pilotScenarios/update', {
      id: payload.id,
      assistantId: assistantId.value,
      template_key: scenario.template_key,
      task_type: scenario.task_type,
      ...payload,
    });
    useAlert('任务已更新');
  } catch (error) {
    useAlert(error?.message || '更新任务失败');
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('pilotScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert('任务已删除');
  } catch (error) {
    useAlert(error?.message || '删除任务失败');
  }
};

const goManageFields = () => {
  router.push(manageFieldsRoute.value);
};

const openEditor = scenario => {
  editingScenario.value = { ...scenario };
  editorOpen.value = true;
};

onMounted(() => {
  fetchData().catch(() => {});
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="pt-4">
      <h2 class="text-base font-semibold text-n-slate-12">AI线索</h2>
      <p class="text-sm text-n-slate-10 mt-2 leading-6">
        AI线索用于识别商机与留资意图，自动接管对话并推进线索收集流程。
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">AI线索模板</h3>
        <button
          type="button"
          class="text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="scenariosCollapsed ? expandSuggestedRules() : closeSuggestedRules()"
        >
          {{ scenariosCollapsed ? '展开模板' : '收起模板' }}
        </button>
      </div>

      <div
        v-if="!scenariosCollapsed"
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3"
      >
        <div
          v-for="item in TASK_TEMPLATES"
          :key="item.templateKey"
          class="rounded-3xl border border-n-slate-4 bg-white dark:bg-n-slate-8 px-5 py-4 flex flex-col gap-3"
        >
          <div
            class="size-11 rounded-2xl bg-n-brand/10 text-n-brand flex items-center justify-center"
          >
            <Icon :icon="item.icon" class="size-5" />
          </div>

          <div class="min-w-0">
            <div class="text-sm text-n-slate-12 font-semibold">
              {{ item.title }}
            </div>
            <div class="text-xs text-n-slate-10 mt-2 leading-4 line-clamp-2">
              {{ item.triggerConditions.join('；') }}
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              :label="existingTemplateKeys.has(item.templateKey) ? '已添加' : '使用'"
              ghost
              slate
              sm
              :disabled="existingTemplateKeys.has(item.templateKey)"
              @click="createTask(item)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">已创建AI线索任务</h3>
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span
            class="inline-flex items-center rounded-full bg-n-slate-2 px-2 py-1 dark:bg-n-slate-7"
          >
            已配置 {{ structuredScenarios.length }}
          </span>
          <span
            class="inline-flex items-center rounded-full bg-n-slate-2 px-2 py-1 dark:bg-n-slate-7"
          >
            启用中 {{ enabledCount }}
          </span>
          <span
            class="inline-flex items-center rounded-full bg-n-slate-2 px-2 py-1 dark:bg-n-slate-7"
          >
            收集字段 {{ fieldCollectionCount }}
          </span>
        </div>
      </div>

      <div v-if="structuredScenarios.length === 0" class="text-sm text-n-slate-10">
        还没有AI线索，请先从上方模板添加。
      </div>

      <TaskScenarioCard
        v-for="scenario in structuredScenarios"
        :key="scenario.id"
        :scenario="scenario"
        :template-meta="templateMap[scenario.template_key]"
        :field-options="fieldOptions"
        @edit="openEditor"
        @delete="deleteScenario"
      />
    </div>

    <TaskScenarioEditorDrawer
      v-model="editorOpen"
      :scenario="editingScenario"
      :template-meta="
        editingScenario ? templateMap[editingScenario.template_key] : null
      "
      :field-options="fieldOptions"
      :is-mip-enabled="isMipEnabled"
      @save="updateStructuredScenario"
      @manage-fields="goManageFields"
    />
  </div>
</template>
